import React from "react";
import ReportService from "../../services/reports/Report.service";
import EpochMonthService from "../../services/EpochMonth.service";
import ReportTable from "./ReportTable.component";
import * as XLSX from "xlsx";

const HIERARCHY_OPTIONS = {
    "Company Wide": [],
    "By Location": ["locationCode"],
    "Location By Class": ["locationCode", "classCode"],
    "By Class": ["classCode"],
    "Class by Location": ["classCode", "locationCode"],
};

const HIERARCHY_COLUMN_NAMES = {
    locationCode: "Location",
    classCode: "Class",
};

const DROPDOWN_LABEL_MAP = {
    locationCode: "Locations",
    classCode: "Classes",
    category: "Categories",
};

const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];

type RawData = {
    locationClassId: number;
    parentLocationClassId: number;
    locationCode: string;
    locationName: string;
    classCode: string;
    className: string;
    category: string;
    epochMonth: number;
    sales: number;
    inflowRetail: number;
    inflowCost: number;
    returnsRetail: number;
    returnsCost: number;
    transferInRetail: number;
    transferInCost: number;
    transferOutRetail: number;
    transferOutCost: number;
    markdowns: number;
    inventory: number;
    receivedRetail: number;
    receivedCost: number;
    inventoryAdjustments: number;
};

type BrokenOutData = Record<string, Record<string, string>>;

type ProcessedData = {
    locationClassId: number;
    parentLocationClassId: number;
    locationCode: string;
    locationName: string;
    classCode: string;
    className: string;
    category: string;
    sales: BrokenOutData;
    inflowRetail: BrokenOutData;
    inflowCost: BrokenOutData;
    returnsRetail: BrokenOutData;
    returnsCost: BrokenOutData;
    transferInRetail: BrokenOutData;
    transferInCost: BrokenOutData;
    transferOutRetail: BrokenOutData;
    transferOutCost: BrokenOutData;
    markdowns: BrokenOutData;
    inventory: BrokenOutData;
    receivedRetail: BrokenOutData;
    receivedCost: BrokenOutData;
    inventoryAdjustments: BrokenOutData;
};

type ParentClassInfo = {
    classCode: string;
    className: string;
};

function breakOutByYearAndMonth(epochMonth: number, value: number): BrokenOutData {
    const date = EpochMonthService.epochMonthToDate(epochMonth);
    const year = date.getFullYear();
    const month = EpochMonthService.epochMonthToMMM(epochMonth);
    return {
        [year]: {
            [month]: Math.round(value).toString(),
        },
    };
}

export function parseRawData(rawData: RawData[]): ProcessedData[] {
    const processedData: ProcessedData[] = rawData.filter((item) => item.epochMonth != null).map((item) => {
        const sales = breakOutByYearAndMonth(item.epochMonth, item.sales);
        // item.inflowRetail/inflowCost are already net (SQL applies -returns +adjustments in its own
        // fallback branch), so only net receivedRetail/receivedCost here - using the already-net value
        // as-is when there's no receivedRetail/receivedCost to net from.
        const calculatedInflowRetail = (item.receivedRetail && item.receivedRetail > 0)
            ? item.receivedRetail - (item.returnsRetail ?? 0) + (item.inventoryAdjustments ?? 0)
            : (item.inflowRetail ?? 0);
        const calculatedInflowCost = (item.receivedCost && item.receivedCost > 0)
            ? item.receivedCost - (item.returnsCost ?? 0) + (item.inventoryAdjustments ?? 0)
            : (item.inflowCost ?? 0);
        const inflowRetail = breakOutByYearAndMonth(item.epochMonth, calculatedInflowRetail);
        const inflowCost = breakOutByYearAndMonth(item.epochMonth, calculatedInflowCost);
        const returnsRetail = breakOutByYearAndMonth(item.epochMonth, item.returnsRetail);
        const returnsCost = breakOutByYearAndMonth(item.epochMonth, item.returnsCost);
        const transferInRetail = breakOutByYearAndMonth(item.epochMonth, item.transferInRetail);
        const transferInCost = breakOutByYearAndMonth(item.epochMonth, item.transferInCost);
        const transferOutRetail = breakOutByYearAndMonth(item.epochMonth, item.transferOutRetail);
        const transferOutCost = breakOutByYearAndMonth(item.epochMonth, item.transferOutCost);
        const markdowns = breakOutByYearAndMonth(item.epochMonth, item.markdowns);
        const inventory = breakOutByYearAndMonth(item.epochMonth, item.inventory);
        const receivedRetail = breakOutByYearAndMonth(item.epochMonth, item.receivedRetail);
        const receivedCost = breakOutByYearAndMonth(item.epochMonth, item.receivedCost);
        const inventoryAdjustments = breakOutByYearAndMonth(item.epochMonth, item.inventoryAdjustments);
        return {
            locationClassId: item.locationClassId,
            parentLocationClassId: item.parentLocationClassId,
            locationCode: item.locationCode,
            locationName: item.locationName,
            classCode: item.classCode,
            className: item.className,
            category: item.category,
            sales,
            inflowRetail,
            inflowCost,
            returnsRetail,
            returnsCost,
            transferInRetail,
            transferInCost,
            transferOutRetail,
            transferOutCost,
            markdowns,
            inventory,
            receivedRetail,
            receivedCost,
            inventoryAdjustments,
        };
    });
    return processedData;
}

function getHierarchyColumnCellContents(item: ProcessedData, hierarchyKey: string, opts?: { useParentClass?: boolean; parentMap?: Map<number, { classCode: string; className: string }> }): string {
    const codeKey = hierarchyKey;
    const nameKey = hierarchyKey === "locationCode" ? "locationName" : "className";
    let code = (item as any)[codeKey];
    let name = (item as any)[nameKey];
    if (opts?.useParentClass && hierarchyKey === "classCode" && opts.parentMap) {
        const parentInfo = opts.parentMap.get(item.parentLocationClassId);
        if (parentInfo) {
            code = parentInfo.classCode;
            name = parentInfo.className;
        }
    }
    return `${code} ${name}`;
}

function getParentClassInfo(item: ProcessedData, parentMap: Map<number, ParentClassInfo>): ParentClassInfo | undefined {
    return parentMap.get(item.parentLocationClassId);
}

function isChildClass(item: ProcessedData, parentMap: Map<number, ParentClassInfo>): boolean {
    const parentInfo = getParentClassInfo(item, parentMap);
    return Boolean(parentInfo && parentInfo.classCode !== item.classCode);
}

function compareHierarchyLabels(left: string, right: string): number {
    return left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" });
}

interface DataValidationReportProps {
    clientId: string;
}

const DropdownMulti: React.FC<{ label: string; options: string[]; selected: string[]; onChange: (v: string[]) => void }> = ({ label, options, selected, onChange }) => {
    const allLabelMap: Record<string, string> = {
        Class: "All Classes",
        Category: "All Categories",
    };

    const pluralize = (word: string) => {
        if (word.toLowerCase().endsWith("s")) return `${word}es`;
        return `${word}s`;
    };

    const getAllLabel = (lbl: string) => allLabelMap[lbl] ?? `All ${pluralize(lbl)}`;

    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const ref = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const onDocClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const safeOnChange = (next: string[]) => {
        const a = [...(selected || [])].sort();
        const b = [...(next || [])].sort();
        if (a.length === b.length && a.every((v, i) => v === b[i])) return;
        onChange(next);
    };

    const toggle = (code: string) => {
        if (code === "ALL") {
            safeOnChange(["ALL"]);
            return;
        }
        const curr = selected.includes("ALL") ? [] : selected;
        if (curr.includes(code)) {
            const next = curr.filter((c) => c !== code);
            safeOnChange(next.length ? next : ["ALL"]);
        } else {
            safeOnChange([...curr, code]);
        }
    };

    const filtered = React.useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return options;
        return options.filter((o) => o.toLowerCase().includes(term));
    }, [options, search]);

    const display = React.useMemo(() => {
        if (selected.includes("ALL") || selected.length === 0) return getAllLabel(label);
        if (selected.length === 1) return selected[0];
        return `${selected.length} ${label}s`;
    }, [selected, label]);

    return (
        <div ref={ref} className="relative w-[220px]">
            <button type="button" onClick={() => setOpen((o) => !o)} className="w-full h-9 px-3 border rounded-md text-sm bg-white flex items-center justify-between">
                <span className="truncate">{display}</span>
                <span className={`ml-2 text-xs ${open ? 'rotate-180' : ''}`}>▾</span>
            </button>
            {open && (
                <div className="absolute z-50 mt-1 left-0 w-[260px] bg-white border rounded-md shadow max-h-64 overflow-auto">
                    <div className="sticky top-0 bg-white p-2 border-b">
                        <input className="w-full px-2 py-1 border rounded text-xs" placeholder={`Search ${label}`} value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                    <ul className="py-1">
                        {filtered.map((opt) => {
                            const isSel = selected.includes("ALL") ? opt === "ALL" : selected.includes(opt);
                            return (
                                <li key={opt} onClick={() => toggle(opt)} className={`px-3 py-1 text-sm cursor-pointer flex items-center justify-between hover:bg-gray-50 ${isSel ? 'bg-gray-50' : ''}`}>
                                    <span className="truncate">{opt === 'ALL' ? getAllLabel(label) : opt}</span>
                                    {isSel && <span className="text-sky-600 text-xs font-semibold">✓</span>}
                                </li>
                            );
                        })}
                    </ul>
                    <div className="sticky bottom-0 bg-white border-t p-2 flex gap-2">
                        <button className="flex-1 text-[11px] bg-gray-100 rounded px-2 py-1" onClick={() => safeOnChange(['ALL'])}>Clear</button>
                        <button className="flex-1 text-[11px] bg-sky-600 text-white rounded px-2 py-1" onClick={() => setOpen(false)}>Done</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export const DataValidationReport: React.FC<DataValidationReportProps> = ({ clientId }) => {
    const rawDataRef = React.useRef<RawData[]>([]);
    const [parsedData, setParsedData] = React.useState<ProcessedData[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);

    const [locationFilter, setLocationFilter] = React.useState<string[] | null>(["ALL"]);
    const [classFilter, setClassFilter] = React.useState<string[] | null>(["ALL"]);
    const [categoryFilter, setCategoryFilter] = React.useState<string[] | null>(["ALL"]);
    const [locationOptions, setLocationOptions] = React.useState<Array<string>>([]);
    const [classOptions, setClassOptions] = React.useState<Array<string>>([]);
    const [categoryOptions, setCategoryOptions] = React.useState<Array<string>>([]);

    const DATA_POINT_ORDER = React.useMemo(() => ([
        "Sales",
        "Markdowns",
        "Inventory EOM",
        "Inflow Retail",
        "Inflow Cost",
        "Returns Retail",
        "Returns Cost",
        "Transfer In Retail",
        "Transfer In Cost",
        "Transfer Out Retail",
        "Transfer Out Cost",
        "Received Retail",
        "Received Cost",
        "Inventory Adjustments",
    ]), []);
    const [dataPointFilter, setDataPointFilter] = React.useState<string[] | null>(["ALL"]);
    const [dataPointOptions, setDataPointOptions] = React.useState<string[]>(["ALL", ...DATA_POINT_ORDER]);
    const [yearsFilter, setYearsFilter] = React.useState<string[] | null>(["ALL"]);
    const [yearsOptions, setYearsOptions] = React.useState<string[]>(["ALL"]);

    const [hierarchy, setHierarchy] = React.useState<string[]>([]);
    const [showChildClasses, setShowChildClasses] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (!clientId) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                const data = await ReportService.getDataValidationReportData({ clientId });
                rawDataRef.current = data.data;

                const processed = parseRawData(data.data);
                setParsedData(processed);

                const locationsSet = new Set<string>();
                const classesSet = new Set<string>();
                const categoriesSet = new Set<string>();
                const yearsSet = new Set<string>();

                const classCodeToName = new Map<string, string>();
                const locationCodeToName = new Map<string, string>();

                data.data.forEach((rd: RawData) => {
                    if (!classCodeToName.has(rd.classCode)) classCodeToName.set(rd.classCode, rd.className);
                    if (!locationCodeToName.has(rd.locationCode)) locationCodeToName.set(rd.locationCode, rd.locationName);

                    locationsSet.add(rd.locationCode);
                    classesSet.add(rd.classCode);
                    if (rd.category) categoriesSet.add(rd.category);

                    if (rd.epochMonth == null) return; // parent row with no actuals — skip year
                    yearsSet.add(String(EpochMonthService.epochMonthToDate(rd.epochMonth).getFullYear()));
                });

                const locationDisplayOptions = Array.from(locationsSet)
                    .map((code) => {
                        const name = locationCodeToName.get(code);
                        return name ? `${name} (${code})` : code;
                    })
                    .sort((a, b) => a.localeCompare(b));
                setLocationOptions(["ALL", ...locationDisplayOptions]);

                const classDisplayOptions = Array.from(classesSet)
                    .map((code) => {
                        const name = classCodeToName.get(code);
                        return name ? `${name} (${code})` : code;
                    })
                    .sort((a, b) => a.localeCompare(b));
                setClassOptions(["ALL", ...classDisplayOptions]);

                const categoryArr = Array.from(categoriesSet).sort((a, b) => a.localeCompare(b));
                setCategoryOptions(["ALL", ...categoryArr]);

                const yearsArr = Array.from(yearsSet).sort((a, b) => Number(b) - Number(a));
                setYearsOptions(["ALL", ...yearsArr]);
            } catch (error) {
                console.error("Error fetching Data Validation Report data:", error);
            }
            setLoading(false);
        };

        fetchData();
    }, [clientId]);

    const parentClassMap = React.useMemo(() => {
        const map = new Map<number, ParentClassInfo>();
        rawDataRef.current.forEach((rd) => {
            if (rd.parentLocationClassId == rd.locationClassId || rd.parentLocationClassId == null) {
                map.set(rd.locationClassId, { classCode: rd.classCode, className: rd.className });
            }
        });
        return map;
    }, [parsedData]);

    const columns = React.useMemo(() => {
        const hierarchyColumns = hierarchy.map((hKey: string) => (hKey in HIERARCHY_COLUMN_NAMES ? HIERARCHY_COLUMN_NAMES[hKey as keyof typeof HIERARCHY_COLUMN_NAMES] : hKey));
        const alwaysColumns = ["Data Point", "Year", ...MONTHS];
        return [...hierarchyColumns, ...alwaysColumns];
    }, [hierarchy]);

    const tableData = React.useMemo(() => {
        const hasClassHierarchy = hierarchy.includes("classCode");
        const shouldIncludeParentAndChildRows = showChildClasses && hasClassHierarchy;

        const keyBuilder = (item: ProcessedData, opts?: { forceParentClass?: boolean }) => {
            const useParentClass = opts?.forceParentClass || (!showChildClasses && hasClassHierarchy);
            const parts = hierarchy.map((hKey) => {
                if (useParentClass && hKey === "classCode") {
                    const parentInfo = parentClassMap.get(item.parentLocationClassId);
                    return parentInfo ? parentInfo.classCode : item.classCode;
                }
                return (item as any)[hKey];
            });
            if (parts.length === 0) return "all";
            return `${useParentClass ? "parent" : "base"}|${parts.join("|")}`;
        };

        const initializeGroupedItem = (item: ProcessedData, opts?: { forceParentClass?: boolean }) => ({
            ...item,
            ...(opts?.forceParentClass
                ? (() => {
                    const parentInfo = parentClassMap.get(item.parentLocationClassId);
                    return parentInfo ? { classCode: parentInfo.classCode, className: parentInfo.className } : {};
                })()
                : {}),
            sales: {},
            inflowRetail: {},
            inflowCost: {},
            returnsRetail: {},
            returnsCost: {},
            transferInRetail: {},
            transferInCost: {},
            transferOutRetail: {},
            transferOutCost: {},
            markdowns: {},
            inventory: {},
            receivedRetail: {},
            receivedCost: {},
            inventoryAdjustments: {},
        });

        const dataPoints = [
            "sales",
            "inflowRetail",
            "inflowCost",
            "returnsRetail",
            "returnsCost",
            "transferInRetail",
            "transferInCost",
            "transferOutRetail",
            "transferOutCost",
            "markdowns",
            "inventory",
            "receivedRetail",
            "receivedCost",
            "inventoryAdjustments",
        ];

        const accumulateIntoGroup = (group: ProcessedData, item: ProcessedData) => {
            dataPoints.forEach((dp) => {
                const brokenOut = (item as any)[dp] as BrokenOutData;
                for (const year in brokenOut) {
                    if (!(year in (group as any)[dp])) {
                        (group as any)[dp][year] = {};
                    }
                    for (const month in brokenOut[year]) {
                        if (!(month in (group as any)[dp][year])) {
                            (group as any)[dp][year][month] = "0";
                        }
                        const existingValue = parseInt((group as any)[dp][year][month], 10) || 0;
                        const newValue = parseInt(brokenOut[year][month], 10) || 0;
                        (group as any)[dp][year][month] = (existingValue + newValue).toString();
                    }
                }
            });
        };

        let groupedData: { [key: string]: ProcessedData } = {};
        return parsedData.reduce((acc: any[], item: ProcessedData) => {
            if (locationFilter && !locationFilter.includes("ALL")) {
                const allowedCodes = locationFilter.map((v) => {
                    const m = /\(([^)]+)\)\s*$/.exec(v);
                    return m ? m[1] : v;
                });
                if (!allowedCodes.includes(item.locationCode)) return acc;
            }
            if (classFilter && !classFilter.includes("ALL")) {
                const allowedCodes = classFilter.map((v) => {
                    const m = /\(([^)]+)\)\s*$/.exec(v);
                    return m ? m[1] : v;
                });
                const parentInfo = parentClassMap.get(item.parentLocationClassId);
                const effectiveClassCode = parentInfo ? parentInfo.classCode : item.classCode;
                if (!allowedCodes.includes(effectiveClassCode) && !allowedCodes.includes(item.classCode)) return acc;
            }
            if (categoryFilter && !categoryFilter.includes("ALL") && !categoryFilter.includes(item.category)) return acc;

            const ensureGroup = (opts?: { forceParentClass?: boolean }) => {
                const key = keyBuilder(item, opts);
                if (!groupedData[key]) {
                    groupedData[key] = initializeGroupedItem(item, opts) as ProcessedData;
                    acc.push(groupedData[key]);
                }
                accumulateIntoGroup(groupedData[key], item);
            };

            if (shouldIncludeParentAndChildRows) {
                ensureGroup({ forceParentClass: true });
                if (isChildClass(item, parentClassMap)) {
                    ensureGroup();
                }
                return acc;
            }

            ensureGroup({ forceParentClass: !showChildClasses && hasClassHierarchy });
            return acc;
        }, []);
    }, [parsedData, hierarchy, locationFilter, classFilter, categoryFilter, showChildClasses, parentClassMap]);

    const sortedTableData = React.useMemo(() => {
        const useParentClass = !showChildClasses && hierarchy.includes("classCode");

        return [...tableData].sort((left, right) => {
            for (const hKey of hierarchy) {
                if (hKey === "classCode" && showChildClasses) {
                    const leftParent = getParentClassInfo(left, parentClassMap);
                    const rightParent = getParentClassInfo(right, parentClassMap);
                    const leftParentLabel = leftParent ? `${leftParent.classCode} ${leftParent.className}` : getHierarchyColumnCellContents(left, hKey);
                    const rightParentLabel = rightParent ? `${rightParent.classCode} ${rightParent.className}` : getHierarchyColumnCellContents(right, hKey);

                    const byParent = compareHierarchyLabels(leftParentLabel, rightParentLabel);
                    if (byParent !== 0) return byParent;

                    const leftIsChild = isChildClass(left, parentClassMap);
                    const rightIsChild = isChildClass(right, parentClassMap);
                    if (leftIsChild !== rightIsChild) return leftIsChild ? 1 : -1;

                    const byClass = compareHierarchyLabels(
                        getHierarchyColumnCellContents(left, hKey),
                        getHierarchyColumnCellContents(right, hKey)
                    );
                    if (byClass !== 0) return byClass;

                    continue;
                }

                const byHierarchy = compareHierarchyLabels(
                    getHierarchyColumnCellContents(left, hKey, { useParentClass, parentMap: parentClassMap }),
                    getHierarchyColumnCellContents(right, hKey, { useParentClass, parentMap: parentClassMap })
                );
                if (byHierarchy !== 0) return byHierarchy;
            }

            return 0;
        });
    }, [tableData, hierarchy, showChildClasses, parentClassMap]);

    const rows = React.useMemo(() => {
        type Row = { meta: Record<string, string>; dataPoint: string; year: string; months: Record<string, string> };
        const out: Row[] = [];
        const pushRowsForDP = (meta: Record<string, string>, dpName: string, dp: BrokenOutData) => {
            const dpAllowed = !dataPointFilter || dataPointFilter.includes("ALL") || dataPointFilter.includes(dpName);
            if (!dpAllowed) return;
            Object.keys(dp)
                .sort((left, right) => Number(right) - Number(left))
                .forEach((year) => {
                    const yearAllowed = !yearsFilter || yearsFilter.includes("ALL") || yearsFilter.includes(String(year));
                    if (!yearAllowed) return;
                    const months: Record<string, string> = {};
                    MONTHS.forEach((m) => (months[m] = dp[year][m] ?? "0"));
                    out.push({ meta, dataPoint: dpName, year, months });
                });
        };
        sortedTableData.forEach((group: ProcessedData) => {
            const meta: Record<string, string> = {};
            hierarchy.forEach((hKey) => (
                meta[HIERARCHY_COLUMN_NAMES[hKey as keyof typeof HIERARCHY_COLUMN_NAMES] || hKey] = getHierarchyColumnCellContents(
                    group,
                    hKey,
                    { useParentClass: !showChildClasses && hierarchy.includes("classCode"), parentMap: parentClassMap }
                )
            ));
            pushRowsForDP(meta, "Sales", group.sales);
            pushRowsForDP(meta, "Markdowns", group.markdowns);
            pushRowsForDP(meta, "Inventory EOM", group.inventory);
            pushRowsForDP(meta, "Inflow Retail", group.inflowRetail);
            pushRowsForDP(meta, "Inflow Cost", group.inflowCost);
            pushRowsForDP(meta, "Returns Retail", group.returnsRetail);
            pushRowsForDP(meta, "Returns Cost", group.returnsCost);
            pushRowsForDP(meta, "Transfer In Retail", group.transferInRetail);
            pushRowsForDP(meta, "Transfer In Cost", group.transferInCost);
            pushRowsForDP(meta, "Transfer Out Retail", group.transferOutRetail);
            pushRowsForDP(meta, "Transfer Out Cost", group.transferOutCost);
            pushRowsForDP(meta, "Received Retail", group.receivedRetail);
            pushRowsForDP(meta, "Received Cost", group.receivedCost);
            pushRowsForDP(meta, "Inventory Adjustments", group.inventoryAdjustments);
        });
        return out;
    }, [sortedTableData, hierarchy, showChildClasses, parentClassMap, dataPointFilter, yearsFilter, DATA_POINT_ORDER]);

    const stickyLeftCount = React.useMemo(() => {
        return hierarchy.length + 2;
    }, [hierarchy.length]);

    const containerRef = React.useRef<HTMLDivElement>(null);

    const exportPayload = React.useMemo(() => {
        const tableColumns = columns.map((c) => ({ name: c }));
        const tableRows: Array<Array<string | number>> = rows.map((row) => {
            const leftMeta = hierarchy.map((hKey) => {
                const label = HIERARCHY_COLUMN_NAMES[hKey as keyof typeof HIERARCHY_COLUMN_NAMES] || hKey;
                return row.meta[label];
            });
            const dp = row.dataPoint;
            const yr = row.year;
            const months = MONTHS.map((m) => row.months[m]);
            return [...leftMeta, dp, yr, ...months];
        });
        return { tableColumns, tableRows };
    }, [columns, rows, hierarchy]);

    const buildExportFileBase = React.useCallback(() => {
        const now = new Date();
        const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
        return `data_validation_${clientId}_${stamp}`;
    }, [clientId]);

    const handleExportExcel = React.useCallback(() => {
        const { tableRows } = exportPayload;
        if (!tableRows.length) return;

        const headers = columns;
        const ws = XLSX.utils.aoa_to_sheet([headers, ...tableRows]);

        const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
        const monthStartCol = hierarchy.length + 2;

        for (let C = range.s.c; C <= range.e.c; ++C) {
            const hdrAddr = XLSX.utils.encode_cell({ r: range.s.r, c: C });
            if (ws[hdrAddr]) ws[hdrAddr].t = "s";
        }

        for (let R = range.s.r + 1; R <= range.e.r; ++R) {
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const addr = XLSX.utils.encode_cell({ r: R, c: C });
                const cell = ws[addr];
                if (!cell) continue;

                if (C >= monthStartCol) {
                    const raw = cell.v;
                    const asNum = typeof raw === "number" ? raw : Number(String(raw).replace(/,/g, "").trim());
                    if (!Number.isNaN(asNum) && String(raw).trim() !== "") {
                        cell.t = "n";
                        cell.v = asNum;
                        (cell as any).z = "0";
                    } else {
                        cell.t = "s";
                    }
                    continue;
                }

                if (C === hierarchy.length) {
                    cell.t = "s";
                    continue;
                }

                if (C === hierarchy.length + 1) {
                    const asYear = Number(String(cell.v).trim());
                    if (!Number.isNaN(asYear)) {
                        cell.t = "n";
                        cell.v = asYear;
                        (cell as any).z = "0";
                    } else {
                        cell.t = "s";
                    }
                    continue;
                }

                cell.t = "s";
            }
        }

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Data Validation");
        XLSX.writeFile(wb, `${buildExportFileBase()}.xlsx`);
    }, [exportPayload, columns, buildExportFileBase, hierarchy.length]);

    const handleExportPdf = React.useCallback(async () => {
        const { tableRows } = exportPayload;
        if (!tableRows.length) return;

        const title = "Data Validation Report";
        const subtitleParts: string[] = [];
        if (hierarchy.length) subtitleParts.push(`Hierarchy: ${hierarchy.map(h => HIERARCHY_COLUMN_NAMES[h as keyof typeof HIERARCHY_COLUMN_NAMES] || h).join(", ")}`);
        if (!showChildClasses && hierarchy.includes("classCode")) subtitleParts.push("Parent Classes");
        if (showChildClasses && hierarchy.includes("classCode")) subtitleParts.push("Child Classes");
        const subtitle = subtitleParts.join(" | ") || undefined;

        await ReportService.tableToPdf({
            columns: columns,
            data: tableRows,
            fileName: buildExportFileBase(),
            title,
            subtitle,
            orientation: "l",
            pageSize: "a0",
        });
    }, [exportPayload, columns, buildExportFileBase, hierarchy, showChildClasses]);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3">
                <h2 className="text-xl font-semibold">Data Validation Report</h2>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={handleExportPdf}
                        disabled={loading || exportPayload.tableRows.length === 0}
                        className="px-3 py-1 border rounded text-sm bg-slate-50 hover:bg-slate-100 disabled:opacity-50"
                    >
                        Export PDF
                    </button>
                    <button
                        type="button"
                        onClick={handleExportExcel}
                        disabled={loading || exportPayload.tableRows.length === 0}
                        className="px-3 py-1 border rounded text-sm bg-slate-50 hover:bg-slate-100 disabled:opacity-50"
                    >
                        Export Excel
                    </button>
                </div>
            </div>

            <div className="flex flex-wrap gap-3 items-center">
                <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">Hierarchy</span>
                    <select
                        className="h-9 px-2 border rounded-md text-sm bg-white"
                        value={JSON.stringify(hierarchy)}
                        onChange={(e) => {
                            const val = e.target.value;
                            try {
                                setHierarchy(JSON.parse(val));
                            } catch {
                                setHierarchy([]);
                            }
                        }}
                    >
                        {Object.entries(HIERARCHY_OPTIONS).map(([label, cols]) => (
                            <option key={label} value={JSON.stringify(cols)}>
                                {label}
                            </option>
                        ))}
                    </select>
                </div>

                <label className="flex items-center gap-2 text-sm text-slate-600">
                    <input
                        type="checkbox"
                        checked={showChildClasses}
                        onChange={(e) => setShowChildClasses(e.target.checked)}
                        disabled={!hierarchy.includes("classCode")}
                    />
                    Show Child Classes
                </label>

                <DropdownMulti
                    label="Location"
                    options={locationOptions}
                    selected={locationFilter || ["ALL"]}
                    onChange={(v) => setLocationFilter(v)}
                />
                <DropdownMulti
                    label="Class"
                    options={classOptions}
                    selected={classFilter || ["ALL"]}
                    onChange={(v) => setClassFilter(v)}
                />
                <DropdownMulti
                    label="Category"
                    options={categoryOptions}
                    selected={categoryFilter || ["ALL"]}
                    onChange={(v) => setCategoryFilter(v)}
                />
                <DropdownMulti
                    label="Data Point"
                    options={dataPointOptions}
                    selected={dataPointFilter || ["ALL"]}
                    onChange={(v) => setDataPointFilter(v)}
                />
                <DropdownMulti
                    label="Year"
                    options={yearsOptions}
                    selected={yearsFilter || ["ALL"]}
                    onChange={(v) => setYearsFilter(v)}
                />
            </div>

            {loading ? (
                <div>Loading Data Validation Report...</div>
            ) : (
                (() => {
                    const { tableColumns, tableRows } = exportPayload;
                    const useVirtualize = tableRows.length > 400;
                    return (
                        <div className="max-w-[1750px]">
                            <ReportTable
                                columns={tableColumns}
                                data={tableRows}
                                stickyHeader
                                headerClassName="px-4 py-2 border border-gray-300 text-left font-medium text-gray-700 bg-white"
                                virtualize={useVirtualize}
                                batchSize={useVirtualize ? 200 : 50}
                            />
                        </div>
                    );
                })()
            )}
        </div>
    );
};